import type {
  SlotEntry,
  SlotLocalId,
  StreamSlot,
  StreetChatStreamInfo,
} from "../types/stream";

/**
 * Every slot position in its fixed order, with the API field behind it. Mirrors
 * Android's `StreetChatStreamInfo` `@SerializedName` mapping exactly.
 */
export const SLOT_FIELDS: ReadonlyArray<
  readonly [SlotLocalId, keyof StreetChatStreamInfo]
> = [
  ["VIDEO_1", "video1Id"],
  ["VIDEO_2", "video2Id"],
  ["AUDIO_1", "audio1Id"],
  ["AUDIO_2", "audio2Id"],
  ["VMPL_FILE_1", "vmplFile1"],
  ["VMPL_FILE_2", "vmplFile2"],
  ["VMPL_LINK_1", "vmplLink1"],
];

/** Present slots, in SLOT_FIELDS order, each with its local id. */
export function slotEntries(
  info: StreetChatStreamInfo | null | undefined,
): SlotEntry[] {
  if (!info) return [];
  const entries: SlotEntry[] = [];
  for (const [localId, field] of SLOT_FIELDS) {
    const slot = info[field];
    if (slot) entries.push({ localId, slot });
  }
  return entries;
}

/** Android's `getStreamSlot(slotLocalId)`. */
export function getSlot(
  info: StreetChatStreamInfo | null | undefined,
  localId: SlotLocalId,
): StreamSlot | undefined {
  const pair = SLOT_FIELDS.find(([id]) => id === localId);
  if (!pair || !info) return undefined;
  return info[pair[1]] ?? undefined;
}

/** Android's `getStreamSlotBySlotId`, returning the local id with it. */
export function getSlotById(
  info: StreetChatStreamInfo | null | undefined,
  slotId: number,
): SlotEntry | undefined {
  return slotEntries(info).find((entry) => entry.slot.id === slotId);
}

const TIMELINE_KEYS = ["playStatus", "seekTime", "playStartTime", "timelineRev"] as const;

const MEDIA_KEYS = ["mediaUrl", "mediaType", "youtubeVideoId", "mediaId", "mediaWidth", "mediaHeight", "itemKind", "provider", "codec", "live"] as const;

/**
 * The keys a newer stored slot keeps over an older write. A post cast moves items on every rev, so
 * an older busy write must not bring its item back. A write that frees the slot only loses its timeline.
 */
function keysKeptOver(older: StreamSlot): readonly (keyof StreamSlot)[] {
  return older.available === true ? TIMELINE_KEYS : [...TIMELINE_KEYS, ...MEDIA_KEYS];
}

/** `next` carries an older cast timeline than `current` (both revs known). */
function olderTimeline(next: StreamSlot, current: StreamSlot): boolean {
  return (
    typeof next.timelineRev === "number" &&
    typeof current.timelineRev === "number" &&
    next.timelineRev < current.timelineRev
  );
}

/**
 * Replaces a conversation's info with `incoming` (a REST fetch) but keeps the
 * stored cast timeline and item of any slot whose stored `timelineRev` is newer.
 * Returns `incoming` itself when nothing needs keeping.
 */
export function keepNewerTimelines(
  incoming: StreetChatStreamInfo,
  stored: StreetChatStreamInfo | undefined,
): StreetChatStreamInfo {
  if (!stored) return incoming;
  let out = incoming;
  for (const [, field] of SLOT_FIELDS) {
    const slot = incoming[field];
    if (!slot) continue;
    const current = getSlotById(stored, slot.id)?.slot;
    if (!current || !olderTimeline(slot, current)) continue;
    const fixed: StreamSlot = { ...slot };
    for (const k of keysKeptOver(slot)) {
      if (k in current) Object.assign(fixed, { [k]: current[k] });
      else delete fixed[k];
    }
    if (out === incoming) out = { ...incoming };
    out[field] = fixed;
  }
  return out;
}

/**
 * Android's `updateStreamSlot`, adapted: MERGES `slot`'s fields into the slot
 * with the same id, rather than replacing it as Android's does — a web event
 * can carry a partial slot, and replacing would drop the fields it left out.
 * Immutable — returns a new object, or `info` itself when no slot matches — so
 * it composes with zustand, which detects changes by reference.
 * An event with an older `timelineRev` keeps the newer cast timeline and item (cast sync).
 */
export function updateStreamSlot(
  info: StreetChatStreamInfo,
  slot: StreamSlot,
): StreetChatStreamInfo {
  for (const [, field] of SLOT_FIELDS) {
    const current = info[field];
    if (current && current.id === slot.id) {
      let incoming: StreamSlot = slot;
      if (olderTimeline(slot, current)) {
        incoming = { ...slot };
        for (const k of keysKeptOver(slot)) delete incoming[k];
      }
      return { ...info, [field]: { ...withoutItemFacts(current, incoming), ...incoming } };
    }
  }
  return info;
}

const ITEM_FACT_KEYS = ["itemKind", "provider", "codec", "live"] as const;

/**
 * A write that names the slot's media describes its item whole: the item facts it leaves out (a photo has no
 * provider, an unknown codec is omitted) must not survive from the previous item.
 */
function withoutItemFacts(current: StreamSlot, incoming: StreamSlot): StreamSlot {
  if (!("mediaUrl" in incoming) || !ITEM_FACT_KEYS.some((k) => k in current)) return current;
  const out = { ...current };
  for (const k of ITEM_FACT_KEYS) delete out[k];
  return out;
}

/** Android's `hasStreaming()`: any present slot is live. */
export function hasStreaming(
  info: StreetChatStreamInfo | null | undefined,
): boolean {
  return slotEntries(info).some((entry) => entry.slot.available === false);
}

/** A live VIDEO slot — what the Streams panel can actually show. */
export function entriesHaveLiveVideo(entries: SlotEntry[]): boolean {
  return entries.some(
    (entry) =>
      entry.localId.startsWith("VIDEO_") && entry.slot.available === false,
  );
}

/** Same check as {@link entriesHaveLiveVideo}, from `info` directly. */
export function hasLiveVideo(
  info: StreetChatStreamInfo | null | undefined,
): boolean {
  return entriesHaveLiveVideo(slotEntries(info));
}

/** Android's `SlotLocalId.isLiveStream()`: VIDEO or AUDIO, played over WHEP. */
export function isLiveStreamSlot(localId: SlotLocalId): boolean {
  return localId.startsWith("VIDEO_") || localId.startsWith("AUDIO_");
}

/** Human label for a slot within its set, e.g. the second video slot is VIDEO_2. */
export function slotLabel(slot: StreamSlot, allSlots: StreamSlot[]): string {
  const sameType = allSlots.filter(
    (candidate) => candidate.streamContentType === slot.streamContentType,
  );
  const index = sameType.findIndex((candidate) => candidate.id === slot.id) + 1;
  return `${slot.streamContentType}_${index}`;
}
